/* eslint-disable lines-between-class-members */
import Component from '@glimmer/component';
import { computed, action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import { alias, sort } from '@ember/object/computed';
import { A } from '@ember/array';
import { getOwner } from '@ember/application';
import { inject as service } from '@ember/service';

import AccountApi from 'mewe/api/account-api';
import FunctionalUtils from 'mewe/shared/functional-utils';
import EnvironmentUtils from 'mewe/utils/environment-utils';
import { getMonthNames } from 'mewe/utils/event-utils';
import isUndefined from 'mewe/utils/isUndefined';
import { isDefined, trimAndLower } from 'mewe/utils/miscellaneous-utils';
import { meweHandleRegex, emailRegex } from 'mewe/shared/utils';
import { TaskStates } from 'mewe/constants';
import dispatcher from 'mewe/dispatcher';
import AuthApi from 'mewe/api/auth-api';
import config from 'mewe/config';

class Errors {
  @tracked emailError = false;
  @tracked newEmailCodeInvalid = false;

  @tracked missingName = false;
  @tracked missingSurname = false;
  @tracked invalidDate = false;
  @tracked handleError = false;
  // password errors
  @tracked invalidPassword = false;
  @tracked invalidSmsCode = false;
  @tracked newPasswordLength = false;
  @tracked passwordMatch = false;
}

export default class AppSettingsAccount extends Component {
  challengeOrigin = 'login';
  arkoseKey = config.arkoseKeyLog;
  hcaptchaSiteKey = config.hcaptcha.login;
  hcaptchaElemClass = 'h-captcha_login';

  @service dynamicDialogs;
  @service account;
  @service tasks;

  @alias('account.activeUser.settings.account') accountSettings;

  // general //
  @tracked errors;
  @tracked attrsInitialised;

  // avatar
  @tracked task;
  @tracked avatarBlob;
  @tracked isSavingAvatar;
  photoDialogTitles = {
    choosePhoto: __('Choose Profile Photo'),
    cropPhoto: __('Edit Profile Photo'),
  };

  // name
  @tracked firstName;
  @tracked lastName;
  @tracked savingName;
  @tracked allowNameChange;

  // password
  @tracked currentPassword;
  @tracked newPassword;
  @tracked repeatPassword;
  @tracked savingPassword;
  @tracked revokeSessions;
  @tracked smsCodeRequired;
  @tracked smsCode;
  @tracked otpButtonDisabled;
  @tracked otpAddress;

  // handle
  @tracked savingHandle;
  @tracked handleHint;
  @tracked handleEditValue;

  // birthday
  @tracked savingBirthDate;
  @tracked allowSeeBirthday;

  // email
  @tracked emailToAdd = '';
  @tracked isSendingCodeInProgress = false;
  @tracked isSubmitCodeInProgress = false;
  @tracked tmpEmailId = null;
  @tracked newEmailCode = '';
  @tracked afterChallengeCallback;
  @tracked challengeReady;

  // other
  @tracked includeCurrentSession = false;
  @tracked editedSetting;

  minPasswordLength = 6;
  minNewPasswordLength = 8;

  constructor() {
    super(...arguments);
    this.task = this.tasks.getTask('photo.avatar.contacts');
    this.selectPhotoBind = this.selectPhoto.bind(this);
  }

  @computed('accountSettings', 'attrsInitialised', 'account.activeUser')
  get isLoaded() {
    if (this.accountSettings && this.account.activeUser && !this.attrsInitialised) {
      this.setInitialAttrs();
    }
    return isDefined(this.accountSettings);
  }

  setInitialAttrs() {
    this.errors = new Errors();

    // name
    this.firstName = this.account.activeUser.firstName;
    this.lastName = this.account.activeUser.lastName;
    // password
    this.currentPassword = '';
    this.newPassword = '';
    this.repeatPassword = '';
    this.smsCode = '';
    this.revokeSessions = false;
    // birthday
    this.days = A();
    for (let i = 1; i <= 31; i++) this.days.push(i);
    this.months = getMonthNames(this.account.activeUser.jsLocale);
    this.birth = this.accountSettings.birth;
    this.allowSeeBirthday = this.accountSettings.birth.allowSeeBirthday;
    // handle
    this.handleEditValue = this.account.activeUser.publicLinkId;
    // email
    this.emailToAdd = this.account.activeUser.primaryEmail;
    this.isSendingCodeInProgress = false;
    this.isSubmitCodeInProgress = false;
    this.tmpEmailId = null;
    this.newEmailCode = '';

    set(this, 'attrsInitialised', true);
  }

  @computed('args.model.sessions')
  get sessions() {
    return this.args.model?.sessions
      ?.filter((session) => session.expires)
      .map((session) => {
        session.isMobile = session.name ? !!~session.name.indexOf('MeWe App') : false;
        return session;
      });
  }

  sessionsSorting = ['expires:desc'];
  @sort('sessions', 'sessionsSorting') sessionsSorted;

  @computed('accountSettings.birth.{month,day}')
  get birthdayText() {
    const month = this.accountSettings?.birth?.month,
      day = this.accountSettings?.birth?.day;

    if (month && day) {
      const d = new Date(Date.UTC(new Date().getUTCFullYear(), month - 1, day, 0, 0, 0));

      return d.toLocaleDateString(this.account.activeUser.jsLocale, {
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      });
    } else {
      return '';
    }
  }

  @computed('accountSettings.birth.month', 'changedMonthId')
  get monthId() {
    if (!this.changedMonthId) {
      const monthId = this.accountSettings?.birth?.month;
      return monthId ? monthId - 1 : null;
    } else {
      return this.changedMonthId - 1;
    }
  }

  @computed('accountSettings.birth.month', 'changedMonthId')
  get monthName() {
    if (this.changedMonthId) {
      return this.months[this.changedMonthId - 1];
    } else {
      const monthId = this.accountSettings?.birth?.month;
      return monthId ? this.months[monthId - 1] : __('Select one');
    }
  }

  @computed('accountSettings.birth.day', 'changedDayNumber')
  get dayNumber() {
    const dayText = this.accountSettings?.birth?.day;
    if (!this.changedDayNumber) {
      return dayText ? dayText : __('Select one');
    } else {
      return this.changedDayNumber;
    }
  }

  @computed(
    'account.birth.{allowSeeBirthday,day,month}',
    'accountSettings.birth.{allowSeeBirthday,day,month}',
    'allowSeeBirthday',
    'changedDayNumber',
    'changedMonthId',
    'errors.{invalidDate,missingFullDate}'
  )
  get isSaveBirthdayDisabled() {
    if (!this.accountSettings?.birth) {
      return true;
    }

    const monthNotChanged = !this.changedMonthId || this.changedMonthId === this.accountSettings.birth.month;
    const dayNotChanged = !this.changedDayNumber || this.changedDayNumber === this.accountSettings.birth.day;
    const birthdayVisibilityNotChanged = this.allowSeeBirthday === this.accountSettings.birth.allowSeeBirthday;

    return (
      this.errors.invalidDate ||
      this.errors.missingFullDate ||
      (monthNotChanged && dayNotChanged && birthdayVisibilityNotChanged)
    );
  }

  @computed('task.state')
  get isUploadingAvatar() {
    return this.task.state === TaskStates.LOADING;
  }

  @computed('accountSettings.publicLinkIdUpdateWaitPeriod')
  get editHandleTimoutDays() {
    const secondsLeft = this.accountSettings.publicLinkIdUpdateWaitPeriod;
    const daysLeft = Math.ceil(secondsLeft / (24 * 60 * 60)); // ceil to avoid 0 days left, 1 day is minimum
    return secondsLeft ? daysLeft : 0;
  }

  changeEditedSetting(setting) {
    this.editedSetting === setting ? (this.editedSetting = null) : (this.editedSetting = setting);

    if (setting === 'birthday') {
      next(() => {
        this.validateBirthDate();
      });
    }

    this.setInitialAttrs();
  }

  validatePassword() {
    let isOk = true;

    if (this.smsCodeRequired) {
      if (!this.smsCode.length) {
        this.errors.invalidSmsCode = true;
        isOk = false;
      }
    } else if (!this.currentPassword || this.currentPassword.length < this.minPasswordLength) {
      this.errors.invalidPassword = true;
      isOk = false;
    } else {
      this.errors.invalidPassword = false;
    }

    if (!this.newPassword || this.newPassword.length < this.minNewPasswordLength) {
      this.errors.newPasswordLength = true;
      isOk = false;
    } else {
      this.errors.newPasswordLength = false;
    }

    if (this.newPassword !== this.repeatPassword) {
      this.errors.passwordMatch = true;
      isOk = false;
    } else {
      this.errors.passwordMatch = false;
    }

    return isOk;
  }

  @action
  submitAvatar() {
    this.isSavingAvatar = true;

    const doneCb = () => {
      this.isSavingAvatar = false;
      this.avatarBlob = null;
    };

    if (this.avatarBlob && this.avatarParams) {
      dispatcher.dispatch('contact', 'setAvatar', this.avatarBlob, this.avatarParams, this.task, doneCb);
    }

    this.changeEditedSetting('photo');

    this.avatarBlob = null;
  }

  selectPhoto(blob, params) {
    this.avatarBlob = blob;
    this.avatarParams = params;
  }

  @action
  validateNameLive() {
    if (this.errors.missingName || this.errors.missingSurname) {
      this.validateName();
    }
  }

  validateName() {
    let isOk = true;

    if (this.firstName.length === 0) {
      this.errors.missingName = true;
      isOk = false;
    } else {
      this.errors.missingName = false;
    }

    if (this.lastName.length === 0) {
      this.errors.missingSurname = true;
      isOk = false;
    } else {
      this.errors.missingSurname = false;
    }

    return isOk;
  }

  @action
  validateSmsCodeLive() {
    if (this.smsCode.length) {
      this.invalidSmsCode = false;
    }
  }

  @action
  validatePasswordLive() {
    if (this.errors.newPasswordLength || this.errors.passwordMatch || this.errors.invalidPassword) {
      this.validatePassword();
    }
  }

  @action
  settingClick(setting) {
    if (setting === 'password') {
      this.smsCode = null;
      this.currentPassword = null;
      this.newPassword = null;
      this.repeatPassword = null;
    }

    this.changeEditedSetting(setting);
  }

  @action
  submitName() {
    if (this.validateName()) {
      this.savingName = true;

      if (this.firstName !== this.account.activeUser.firstName || this.lastName !== this.account.activeUser.lastName) {
        this.saveName();
      } else {
        this.changeEditedSetting('name');
        this.savingName = false;
      }
    }
  }

  saveName() {
    AccountApi.setData({
      firstName: this.firstName,
      lastName: this.lastName,
    })
      .then(() => {
        FunctionalUtils.info(__('Changes in your account have been saved'));
        location.reload();
      })
      .catch(() => {
        FunctionalUtils.error(__('An error occurred'));
      })
      .finally(() => {
        next(() => {
          this.savingName = false;
        });
      });
  }

  @action
  savePassword() {
    if (!this.validatePassword()) {
      return;
    }

    this.savingPassword = true;

    const revokeAllSessions = this.revokeSessions;
    const params = {
      newPassword: this.newPassword,
    };

    if (this.smsCodeRequired) {
      params.code = this.smsCode;
    } else {
      params.oldPassword = this.currentPassword;
    }

    AccountApi.setPassword(params, revokeAllSessions)
      .then(() => {
        FunctionalUtils.info(__('Password set successfully'));
        this.changeEditedSetting('password');
      })
      .then(() => {
        if (revokeAllSessions) {
          window.location.reload();
        }

        this.smsCodeRequired = false;
      })
      .catch((data) => {
        if (this.smsCodeRequired) {
          const resp = data.data;
          if (resp && resp.message === 'Wrong validation code') {
            this.errors.invalidSmsCode = true;
          }
        } else {
          this.errors.invalidPassword = true;
        }
      })
      .finally(() => {
        this.smsCode = null;
        this.currentPassword = null;
        this.newPassword = null;
        this.repeatPassword = null;

        next(this, function () {
          this.savingPassword = false;
        });
      });
  }

  get handleRequirementsText() {
    return __(`Username must be between 4-16 characters, can only consist letters, numbers and underscores.`);
  }

  get saveHandleDisabled() {
    const minLength = 4;
    return this.savingHandle || !this.accountSettings.canUpdatePublicLinkId || this.handleEditValue.length < minLength;
  }

  @action
  handleChanged() {
    this.handleEditValue = trimAndLower(this.handleEditValue);
    this.errors.handleError = false;
  }

  @action
  submitHandle() {
    const newHandle = trimAndLower(this.handleEditValue);

    if (!meweHandleRegex.test(newHandle)) {
      this.errors.handleError = true;
      this.handleHint = this.handleRequirementsText;
      return false;
    }

    if (isUndefined(this.account.activeUser.badges.verified)) {
      this.saveHandle();
    } else {
      this.dynamicDialogs.openDialog('simple-dialog-new', {
        okButtonText: __('Change Username'),
        cancelButtonText: __('Cancel'),
        title: __('Verification Status'),
        message: __('If you change your username your account will no longer be verified.'),
        imagePath: '/assets/images/icons/badge-verified.png',
        classImg: 'mt-24 mb-8',
        imgHeight: 24,
        imgWidth: 24,
        onConfirm: () => {
          this.saveHandle();
        },
      });
    }
  }

  saveHandle() {
    this.savingHandle = true;

    const newHandle = trimAndLower(this.handleEditValue);

    const successCallback = () => {
      dispatcher.dispatch('client-data', 'saveClientPreference', 'publicLinkId', newHandle);
      dispatcher.dispatch('client-data', 'saveClientPreference', 'settings.account.publicLinkId', newHandle);
      dispatcher.dispatch('client-data', 'saveClientPreference', 'settings.account.canUpdatePublicLinkId', false);
      dispatcher.dispatch(
        'client-data',
        'saveClientPreference',
        'settings.account.publicLinkIdUpdateWaitPeriod',
        30 * 24 * 60 * 60
      ); // 30 days

      this.changeEditedSetting('handle');
    };

    const finallyCallback = () => {
      this.savingHandle = false;
    };

    AccountApi.setData({ publicLinkId: newHandle })
      .then(() => {
        successCallback();

        if (!isUndefined(this.account.activeUser.badges.verified)) {
          location.reload();
        }
      })
      .catch((error) => {
        if (error?.status === 409) {
          const available = error.data?.available;
          this.handleHint = __('The username is taken or invalid. We suggest you use: {available}', {
            available: available,
          });
          this.errors.handleError = true;
        } else if (error?.status === 400) {
          if (error.data?.errorCode === 121) {
            this.handleHint = __(
              'You can not change your username right now because you’ve already changed it within the last 60 days.'
            );
          } else {
            this.handleHint = __('The username is taken or invalid.');
          }
          this.errors.handleError = true;
        } else {
          FunctionalUtils.showDefaultErrorMessage();
        }
      })
      .finally(finallyCallback);
  }

  validateBirthDate() {
    let year = 2012;
    let month = this.monthId;
    let day = this.dayNumber;
    let date = new Date(year, month, day);

    // without error, because one of fields was not set yet at all
    if (month === null || day === __('Select one')) {
      set(this, 'errors.missingFullDate', true);
      return false;
    } else {
      set(this, 'errors.missingFullDate', false);
    }

    if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
      set(this, 'errors.invalidDate', false);
      return true;
    } else {
      set(this, 'errors.invalidDate', true);
      return false;
    }
  }

  @action
  submitBirthDate() {
    if (this.validateBirthDate()) {
      this.savingBirthDate = true;

      if (
        this.changedDayNumber !== this.accountSettings.birth.day ||
        this.changedMonthId !== this.accountSettings.birth.month ||
        this.allowSeeBirthday !== this.accountSettings.birth.allowSeeBirthday
      ) {
        this.saveBirthDate();
      } else {
        this.changeEditedSetting('birthday');
        this.savingBirthDate = false;
      }
    } else {
      this.changeEditedSetting('birthday');
      this.savingBirthDate = false;
    }
  }

  @action
  saveBirthDate() {
    const params = {
      birthDay: this.changedDayNumber || this.accountSettings.birth.day,
      birthMonth: this.changedMonthId || this.accountSettings.birth.month,
      allowSeeBirthday: this.allowSeeBirthday,
    };

    AccountApi.setData(params)
      .then(() => {
        FunctionalUtils.info(__('Changes in your account have been saved'));

        dispatcher.dispatch('client-data', 'saveClientPreference', 'settings.account.birth', {
          day: params.birthDay,
          month: params.birthMonth,
          allowSeeBirthday: params.allowSeeBirthday,
        });
        this.changeEditedSetting('birthday');
      })
      .catch(() => {
        FunctionalUtils.showDefaultErrorMessage();
      })
      .finally(() => {
        next(() => {
          this.savingBirthDate = false;
        });
      });
  }

  @action
  changeBirth(type, index) {
    set(this, type, parseInt(index, 10));
    this.validateBirthDate();
  }

  @action
  revokeSession(id) {
    AccountApi.revokeSession(id)
      .then(() => {
        const route = getOwner(this).lookup('route:app.settings');
        if (route) {
          route.send('reloadModel');
        }
      })
      .catch(() => FunctionalUtils.showDefaultErrorMessage());
  }

  @action
  revokeAllSessions() {
    AccountApi.revokeAllSessions(this.includeCurrentSession)
      .then(() => {
        const route = getOwner(this).lookup('route:app.settings');
        if (route) {
          route.send('reloadModel');
        }
      })
      .catch(() => FunctionalUtils.showDefaultErrorMessage());
  }

  get emailToAddValue() {
    return this.emailToAdd.trim();
  }

  get isNewEmailValid() {
    return emailRegex.test(this.emailToAddValue);
  }

  @action
  emailUpdated() {
    this.errors.emailError = false;
  }

  @action
  sendEmailCode() {
    if (this.errors.emailError || this.isSendingCodeInProgress) {
      return;
    }

    const doSend = (challengeToken, provider, onChallengeFailed) => {
      this.isSendingCodeInProgress = true;

      const params = {
        email: this.emailToAddValue,
        session_token: challengeToken,
        challenge_provider: provider,
      };

      AuthApi.getRegistrationEmailCode(params)
        .then((data) => {
          this.tmpEmailId = data.tmpEmailId;
        })
        .catch((error) => {
          this.errors.emailError = false;

          const resp = error.data;

          if (error.status === 400 && (resp?.errorCode == 117 || resp?.errorCode == 118)) {
            onChallengeFailed?.();
          } else if (resp.errorCode) {
            if (resp.errorCode === 100) {
              this.errors.emailError = __('This email is invalid');
            } else if (resp.errorCode === 109) {
              this.errors.emailError = __('This email is already taken');
            }
          } else if (error.status === 420) {
            this.errors.emailError = __(`You've exceeded the number of attempts. Please try again later`);
          } else {
            FunctionalUtils.showDefaultErrorMessage();
          }
        })
        .finally(() => {
          this.isSendingCodeInProgress = false;
        });
    }

    this.afterChallengeCallback = doSend.bind(this);
  }

  @action
  submitNewEmailCode() {
    if (this.submitCodeInProgress) {
      return;
    }

    this.isSubmitCodeInProgress = true;

    AccountApi.setEmail({
      tmpEmailId: this.tmpEmailId,
      code: this.newEmailCode,
    })
      .then((data) => {
        dispatcher.dispatch('client-data', 'saveClientPreference', 'primaryEmail', data.email);
        FunctionalUtils.info(__('Email updated successfully'));
        this.changeEditedSetting('email');
      })
      .catch((error) => {
        this.errors.newEmailCodeInvalid = '';

        if (error.data.errorCode === 151) {
          this.errors.newEmailCodeInvalid = __('Please use a valid code.');
        } else if (error.data.limitReached) {
          this.errors.newEmailCodeInvalid = __(`You've exceeded the number of attempts. Please try again later`);
        } else {
          FunctionalUtils.showDefaultErrorMessage();
        }
      })
      .finally(() => {
        this.isSubmitCodeInProgress = false;
      });
  }

  @action
  challengeReadyCallback() {
    this.challengeReady = true;
  }
}
